/* eslint-disable @typescript-eslint/no-empty-function */
// import { di } from '@intigral/domain';
import { DI } from '@intigral/constant/config/DIContainer';
import { container } from '@intigral/domain/lib/DataDomain';
import { IMpxService } from '@intigral/domain/lib/application/services/MpxService';
import { IReminderService } from '@intigral/domain/lib/application/services/ReminderService';
import { FlexWrapper, RemindItem, Spinner, Text } from '@intigral/shared';
import { REMINDER_ITEM_CATEGORY } from '@libs/constants';
import { useInfiniteQuery } from '@tanstack/react-query';
import moment from 'moment';
import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import h from '../../helpers/common';
import f from '../../helpers/formatter';
import f2 from '../../helpers/formatter2';
import url from '../../helpers/url';
import { showUpgradedModal } from '../../store/reducers/generalConfigurationSlice';
import { transformMpxEntries } from '../../utils/transformResponse/mpxResponses';
import { getUserInfo } from '../../utils/user';
import { VodCarousel } from './VodCarousel';
import { PPVReminderCarousel } from './PPVReminderCarousel';
import { Carousel2 } from '@intigral/ui';
import { SectionHeaderWithPagination } from '@intigral/ui/components/SectionHeaderWithPagination';
import { transformPPVData } from '../../utils/transformResponse/ppvTransformer';
import usePPVManagement from '../../hooks/usePPVManagement';
import useLanguage from '../../hooks/useLanguage';

const PAGINATION_LIMIT = 10;

const IMAGE_LINEAR_WIDTH = 286;
const IMAGE_LINEAR_HEIGHT = 190;

const transformLinearData = (
    reminder: any,
    userChannels: any,
    language: string,
    dispatch: any,
    shouldShowLockFn: any
) => {
    const startTime = reminder?.reminderListing?.startTime
        ? moment(reminder.reminderListing.startTime).format('MMM DD HH:mm')
        : '';
    const endTime = reminder?.reminderListing?.endTime
        ? moment(reminder.reminderListing.endTime).format('MMM DD HH:mm')
        : '';

    let startDayLabel = '',
        startTimeLabel = '',
        endDayLabel = '',
        endTimeLabel = '';

    if (startTime && endTime) {
        startDayLabel = startTime.match(/([^\s]+\s[^\s]+)\s([^\s]+)/)[1];
        startTimeLabel = startTime.match(/([^\s]+\s[^\s]+)\s([^\s]+)/)[2];
        endDayLabel = endTime.match(/([^\s]+\s[^\s]+)\s([^\s]+)/)[1];
        endTimeLabel = endTime.match(/([^\s]+\s[^\s]+)\s([^\s]+)/)[2];
    }

    // Use the PPV management hook function passed as parameter
    const channelId = reminder.channelId;
    const { isLocked } = shouldShowLockFn(reminder, channelId);

    return {
        Id: reminder.id,
        mainImage: {
            blurDataURL: 'data:image/webp;base64,...', // Your existing blur data URL
            src: h.getImagizerUrl(reminder?.images?.[0]?.url, IMAGE_LINEAR_WIDTH, IMAGE_LINEAR_HEIGHT),
            height: IMAGE_LINEAR_HEIGHT,
            width: IMAGE_LINEAR_WIDTH,
        },
        title: h.getLang() == 'en' ? reminder?.title : reminder?.titleLocalized?.ar,
        description: h.getLang() == 'en' ? reminder?.description : reminder?.descriptionLocalized?.ar,
        timeStamp: `${startDayLabel} . ${startTimeLabel} - ${endDayLabel} . ${endTimeLabel}`,
        href: url.route(url.getDetailsPage('live'), { channelId: btoa(reminder.channelId) }),
        isLocked,
        onLockClick: () => dispatch(showUpgradedModal(true)),
        showBellIcon: startTime && endTime && true,
    };
};

function removeDuplicates(array, key) {
    const unique = [];
    const keys = new Set();

    array.forEach((item) => {
        const value = JSON.stringify(key ? item[key] : item); // Use key or the whole object for uniqueness
        if (!keys.has(value)) {
            keys.add(value);
            unique.push(item);
        }
    });

    return unique;
}

export const RemindersCarousel = (props: any) => {
    // const [contentToShow, setContentToShow] = useState([]);
    const reminderType = props?.meta?.reminder?.type ?? 'VOD';

    const isLinear = reminderType == REMINDER_ITEM_CATEGORY.LINEAR;
    const isPPVReminder = reminderType == REMINDER_ITEM_CATEGORY.PPV;
    const language = useLanguage();
    const swiperElement = useRef(null);
    const userChannels = useSelector((state: any) => state.liveTv.userChannels.channels) ?? [];
    const dispatch = useDispatch();

    // Use PPV management hook for all PPV-related functionality
    const { shouldShowLock, enableLockIcon } = usePPVManagement();

    const {
        isLoading: isRemindersLoading,
        isError,
        error,
        data,
        fetchNextPage,
        hasNextPage,
    } = useInfiniteQuery({
        queryKey: ['remindersList', reminderType],
        initialPageParam: 1,
        queryFn: async ({ pageParam }) => {
            const userInfo = getUserInfo();
            const reminderService = container.resolve<IReminderService>(DI.SERVICES.IReminderService);
            const reminderResponse = await reminderService.listReminder(
                userInfo.apiKey,
                { category: reminderType, detailed: true },
                userInfo.authToken,
                userInfo.identityToken
            );

            if (!isLinear && !isPPVReminder && reminderResponse.data?.reminders?.length > 0) {
                const guids = f2.pluck(reminderResponse.data.reminders, 'guid');
                const mpxService = container.resolve<IMpxService>(DI.SERVICES.IMpxService);
                const mpxResponse = await mpxService.getMpxAllAssets({ byGUID: guids.join('|') });
                const transformedEntries = transformMpxEntries(mpxResponse);

                return {
                    ...reminderResponse,
                    data: {
                        ...reminderResponse.data,
                        transformedContent: transformedEntries,
                    },
                };
            }
            return {
                ...reminderResponse,
                data: {
                    ...reminderResponse.data,
                    transformedContent: reminderResponse.data.reminders.map((i) => {
                        return isPPVReminder
                            ? transformPPVData(i, userChannels, enableLockIcon)
                            : transformLinearData(i, userChannels, language, dispatch, shouldShowLock);
                    }),
                },
            };
        },
        getNextPageParam: (lastPage: any, allPages, lastPageParam) => {
            const nextStart = lastPageParam + PAGINATION_LIMIT + 1;
            if (nextStart >= lastPage.data?.totalEntries) return null;
            return nextStart;
        },
    });

    const swiperSettings = {
        slidesPerView: 1,
        breakpoints: {
            560: {
                slidesPerView: 2,
            },
            824: {
                slidesPerView: 3,
            },
            1212: {
                slidesPerView: 4,
            },
            1500: {
                slidesPerView: 5,
            },
        },
        loop: false,
    };

    if (isRemindersLoading) {
        return (
            <div className="container full-width">
                <SectionHeaderWithPagination isLoading={true} />
                <Carousel2 isLoading={true} swiperSettings={swiperSettings}>
                    <RemindItem isLoading={true} />
                </Carousel2>
            </div>
        );
    }

    if (isError) {
        return 'reminder error';
    }

    const contentToShow =
        removeDuplicates(data?.pages.flatMap((page) => page.data.transformedContent) || [], 'Id') ?? [];

    return (() => {
        if (contentToShow.length > 0) {
            if (isLinear) {
                return (
                    <div id="reminder-carousel" className="container full-width" data-test-id={props?.carousel?.id}>
                        <FlexWrapper gap="2x" justifyContent="between">
                            <Text
                                as="h2"
                                variant="h2"
                                alignment="start"
                                margin={{ default: '2x', md: '3x' }}
                                size={{ default: '18', md: '28' }}
                            >
                                {f.getComponentTitle(props.meta)}
                            </Text>
                            {isRemindersLoading && (
                                <div style={{ display: 'flex', margin: '-12px 0 6px' }}>
                                    <Spinner block isAutoHeight />
                                </div>
                            )}
                        </FlexWrapper>
                        <Carousel2
                            ref={swiperElement}
                            swiperSettings={swiperSettings}
                            dir={language === 'ar' ? 'rtl' : 'ltr'}
                            reachEnd={() => {
                                if (hasNextPage) fetchNextPage();
                            }}
                        >
                            {contentToShow.map((item) => (
                                <RemindItem
                                    key={item.Id}
                                    href={item.href}
                                    image={item.mainImage?.src}
                                    title={item.title}
                                    description={item.description}
                                    when={item.timeStamp}
                                    showBell={item.showBellIcon}
                                    locked={item.isLocked}
                                    onClick={(e) => {
                                        if (item.isLocked) {
                                            e.preventDefault();
                                            item.onLockClick?.();
                                        }
                                    }}
                                />
                            ))}
                        </Carousel2>
                    </div>
                );
            } else if (isPPVReminder) {
                return (
                    <PPVReminderCarousel
                        content={contentToShow}
                        meta={{
                            title: {
                                [h.getLang()]: f.getComponentTitle(props?.meta),
                            },
                        }}
                        carousel={props?.carousel}
                        language={language}
                    />
                );
            } else {
                return (
                    <VodCarousel
                        content={contentToShow}
                        meta={{
                            title: {
                                [h.getLang()]: f.getComponentTitle(props.meta),
                            },
                        }}
                        carousel={props?.carousel}
                    />
                );
            }
        }
    })();
};
